import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { TextStyle } from '@tiptap/extension-text-style';
import FontFamily from '@tiptap/extension-font-family';
import IndentExtension from './IndentExtension';
import LineHeightExtension from './LineHeightExtension';
import { FontSizeExtension } from './FontSizeExtension';
import { DateTimeExtension } from './DateTimeExtension';
import { PasteImageExtension } from './PasteImageExtension';

/**
 * Image extension with width attribute support.
 * Tiptap's default Image only allows src/alt/title — width gets stripped on load.
 * parseHTML reads both the width attribute and inline style so existing notes render correctly.
 * renderHTML writes width as inline style (consistent with what ImageContextMenu sets via updateAttributes).
 */
const ImageWithWidth = Image.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            width: {
                default: null,
                parseHTML: (element) => element.getAttribute('width') || element.style.width || null,
                renderHTML: (attributes) => {
                    if (!attributes.width) return {};
                    return { style: `width: ${attributes.width}` };
                },
            },
        };
    },
});

/**
 * createEditor — factory for the BodyEditor tiptap instance.
 * @param {string} content - initial HTML content
 * @param {function} onTransaction - called on every editor transaction
 * @returns {Editor}
 */
export function createEditor(content, onTransaction) {
    return new Editor({
        extensions: [
            StarterKit,
            ImageWithWidth.configure({ inline: true, allowBase64: true }),
            TextStyle,
            FontFamily,
            FontSizeExtension,
            IndentExtension,
            LineHeightExtension,
            DateTimeExtension,
            PasteImageExtension,
        ],
        content: content ?? '',
        onTransaction,
        enableInputRules: false,    // disable markdown shortcuts (- tab → bullet, etc.)
    });
}
